import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Car, Plus, XCircle } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const statusStyles = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  completed: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/bookings/me").then((res) => setBookings(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.put(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const upcoming = bookings.filter((b) => ["pending", "confirmed"].includes(b.status)).length;

  return (
    <div className="bg-background min-h-screen" data-testid="customer-dashboard">
      <Navbar />

      <section className="pt-32 pb-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-between items-end gap-6">
          <div>
            <p className="text-xs tracking-label uppercase gold-text mb-3">My Account</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight">
              Welcome, {user?.name?.split(" ")[0]}
            </h1>
          </div>
          <Link
            to="/fleet"
            data-testid="new-booking-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-[3px] py-[1px] font-medium tracking-wide"
          >
            <Plus size={18} /> New Booking
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          {[
            { label: "Total Bookings", value: bookings.length, icon: Car },
            { label: "Upcoming", value: upcoming, icon: Calendar },
            { label: "Completed", value: bookings.filter((b) => b.status === "completed").length, icon: Clock },
          ].map((s) => (
            <div key={s.label} className="bg-[#121214] border border-white/10 rounded-sm p-6 flex items-center justify-between" data-testid={`cust-stat-${s.label}`}>
              <div>
                <div className="font-heading text-4xl gold-text">{s.value}</div>
                <div className="text-xs tracking-label uppercase text-zinc-500 mt-1">{s.label}</div>
              </div>
              <s.icon size={28} strokeWidth={1.5} className="text-zinc-600" />
            </div>
          ))}
        </div>

        <h2 className="font-heading text-3xl font-light mb-6">Your Bookings</h2>

        {loading ? (
          <div className="py-20 text-center font-mono text-sm tracking-widest gold-text animate-pulse">LOADING…</div>
        ) : bookings.length === 0 ? (
          <div className="bg-[#121214] border border-white/10 rounded-sm p-16 text-center" data-testid="no-bookings">
            <Car size={40} strokeWidth={1} className="mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400 font-light">No bookings yet. Reserve your first ride.</p>
            <Link to="/fleet" className="inline-block mt-6 gold-text hover:underline">Browse the fleet →</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-${b.id}`} className="bg-[#121214] border border-white/10 rounded-sm overflow-hidden flex flex-col sm:flex-row hover:border-white/20 transition-colors">
                <div className="sm:w-48 h-40 sm:h-auto shrink-0">
                  <img src={b.vehicle_image} alt={b.vehicle_name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex-1 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading text-2xl">{b.vehicle_name}</h3>
                      <span className={`text-xs px-3 py-1 rounded-sm border capitalize ${statusStyles[b.status]}`} data-testid={`status-${b.id}`}>{b.status}</span>
                    </div>
                    <p className="text-zinc-400 text-sm flex items-center gap-2"><MapPin size={14} className="gold-text" /> {b.pickup_location} → {b.dropoff_location}</p>
                    <p className="text-zinc-400 text-sm flex items-center gap-2"><Calendar size={14} className="gold-text" /> {b.pickup_date} at {b.pickup_time} · {b.hours}h · {b.passengers} pax</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="gold-text font-mono text-xl">${b.total_price}</div>
                    {["pending", "confirmed"].includes(b.status) && (
                      <button onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`} className="mt-3 text-zinc-400 hover:text-red-400 text-sm flex items-center gap-1 ml-auto transition-colors">
                        <XCircle size={14} /> Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
